import { Directive, HostListener, inject, Input } from '@angular/core';

import mixpanel from 'mixpanel-browser';

import { ANALYTICS_USER, BillingType } from '@bn/primitives';

@Directive({
  selector: '[mixpanelEvent]',
})
export class MixpanelEventsDirective {
  readonly #analyticsUser = inject(ANALYTICS_USER, { optional: true });

  @Input() mixpanelEvent!: string;
  @Input() mixpanelEventProperties: Record<string, string | number> = {};

  /**
   * Automatically tracks a Mixpanel event when the host element is clicked.
   * Uses the provided @Input mixpanelEvent and mixpanelEventProperties.
   *
   * @param onClickEvent The MouseEvent triggered by the click.
   */
  @HostListener('click', ['$event'])
  onClick(onClickEvent: MouseEvent) {
    if (onClickEvent && this.mixpanelEvent) {
      this.trackEvent(this.mixpanelEvent, this.mixpanelEventProperties);
    }
  }

  /**
   * Programmatically triggers a Mixpanel tracking event.
   * Can be used to track custom interactions beyond click events in templates.
   *
   * @param mixpanelEvent - The name of the Mixpanel event (without the 'Dash: ' prefix).
   * @param mixpanelEventProperties - Additional properties to associate with the event.
   */
  public addEvent(mixpanelEvent: string, mixpanelEventProperties: Record<string, string | number>): void {
    const userDetails = this.#analyticsUser?.() ?? {};
    const billingTypeName = typeof userDetails.BillingType === 'number' && userDetails.BillingType in BillingType ? BillingType[userDetails.BillingType] : '';

    this.trackEvent(mixpanelEvent, {
      ...mixpanelEventProperties,
      distinct_id: userDetails.Id ?? '',
      BillingType: billingTypeName,
    });
  }

  private trackEvent(event: string, properties: Record<string, string | number>): void {
    mixpanel.track(`Dash: ${event}`, properties);
  }
}
